import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Icon from '@mui/material/Icon';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'components/DashboardNavbar';
import Footer from 'examples/Footer';
import DateFilterPresets from 'components/DateFilterPresets';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';

import { listPastSessions, listBetLimitPresets } from 'services/nh-room-dashboard';
import {
  useDashboardTheme, ACCENT_COLOR, accentBarSx, cardSx,
  panelHeaderSx, countBadgeSx, tableHeadRowSx, tableRowSx, scrollbarSx, footerBarSx,
  fmt, fmtBtc, fmtDateShort
} from 'layouts/nh-room-dashboard/utils';

function NHSessionHistory() {
  const navigate = useNavigate();
  const t = useDashboardTheme();

  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [page, setPage] = useState(0);
  const limit = 20;

  // Filters
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [preset, setPreset] = useState('alltime');
  const [betLimitId, setBetLimitId] = useState('');
  const [betLimits, setBetLimits] = useState([]);

  // Load bet limit presets on mount
  useEffect(() => {
    (async () => {
      const presets = await listBetLimitPresets();
      setBetLimits(Array.isArray(presets) ? presets : []);
    })();
  }, []);

  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchSessions = useCallback(async (isBackground = false) => {
    if (!isBackground) setLoading(true);
    const params = { limit, offset: page * limit };
    if (startDate) params.startDate = startDate.toISOString();
    if (endDate) params.endDate = endDate.toISOString();
    if (betLimitId) params.betLimitId = betLimitId;
    const res = await listPastSessions(params);
    setSessions(res.data || []);
    setTotal(res.total || 0);
    setLoading(false);
    setInitialLoading(false);
  }, [page, startDate, endDate, betLimitId]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Auto-refresh polling (30s) — background fetch, no skeleton flicker
  useEffect(() => {
    if (!autoRefresh) return;
    const timer = setInterval(() => fetchSessions(true), 30000);
    return () => clearInterval(timer);
  }, [autoRefresh, fetchSessions]);

  const handleDateChange = (start, end, presetKey) => {
    setStartDate(start);
    setEndDate(end);
    if (presetKey) setPreset(presetKey);
    setPage(0);
  };

  const handleBetLimitChange = (e) => {
    setBetLimitId(e.target.value);
    setPage(0);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={{ xs: 1, md: 3 }}>
        {/* Header Card */}
        <Card sx={{ ...cardSx, mb: 3 }}>
          <MDBox sx={accentBarSx} />
          <MDBox sx={{ p: { xs: 2, sm: 3 } }}>
            <MDBox display='flex' justifyContent='space-between' alignItems={{ xs: 'flex-start', sm: 'center' }} flexWrap='wrap' gap={2} mb={3}>
              <MDBox display='flex' alignItems='center' gap={{ xs: 1.5, sm: 2 }}>
                <MDBox
                  sx={{
                    width: { xs: '40px', sm: '48px' },
                    height: { xs: '40px', sm: '48px' },
                    borderRadius: '14px',
                    background: ACCENT_COLOR,
                    boxShadow: '0 4px 12px rgba(226,0,27,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <Icon sx={{ color: 'white !important', fontSize: '1.5rem !important' }}>history</Icon>
                </MDBox>
                <MDBox>
                  <MDTypography variant='h5' fontWeight='bold' sx={{ letterSpacing: '-0.01em', lineHeight: 1.2, fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
                    Crypto Express — Session History
                  </MDTypography>
                  <MDTypography variant='body2' color='text' sx={{ opacity: 0.6, fontSize: '0.8rem', display: { xs: 'none', sm: 'block' } }}>
                    All past and active mining sessions
                  </MDTypography>
                </MDBox>
              </MDBox>
              <MDBox display='flex' alignItems='center' gap={1.5}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={autoRefresh}
                      onChange={(e) => setAutoRefresh(e.target.checked)}
                      color='info'
                      size='small'
                    />
                  }
                  label={
                    <MDTypography variant='button' color='text' sx={{ fontSize: '0.72rem' }}>
                      Auto-refresh
                    </MDTypography>
                  }
                />
                <MDButton
                  variant='outlined'
                  color='info'
                  size='small'
                  onClick={fetchSessions}
                  disabled={loading}
                  sx={{ borderRadius: '10px', fontWeight: 600, fontSize: '0.72rem' }}
                >
                  <Icon sx={{ mr: 0.5, fontSize: '1rem !important' }}>refresh</Icon>
                  Refresh
                </MDButton>
              </MDBox>
            </MDBox>

            {/* Date Range Filter */}
            <MDBox
              mb={2}
              sx={{
                p: 2,
                borderRadius: '12px',
                background: t.isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                border: `1px solid ${t.divider}`
              }}
            >
              <MDBox display='flex' alignItems='center' gap={0.75} mb={1.5}>
                <Icon sx={{ fontSize: '0.9rem !important', color: t.isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)' }}>date_range</Icon>
                <MDTypography variant='button' fontWeight='bold' sx={{ fontSize: '0.68rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: t.isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }}>
                  Date Range
                </MDTypography>
              </MDBox>
              <DateFilterPresets
                onDateChange={handleDateChange}
                preset={preset}
                startDate={startDate}
                endDate={endDate}
              />
            </MDBox>

            {/* Bet Limit / Preset Filter */}
            <MDBox
              sx={{
                p: 2,
                borderRadius: '12px',
                background: t.isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                border: `1px solid ${t.divider}`
              }}
            >
              <MDBox display='flex' alignItems='center' gap={0.75} mb={1.5}>
                <Icon sx={{ fontSize: '0.9rem !important', color: t.isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)' }}>filter_list</Icon>
                <MDTypography variant='button' fontWeight='bold' sx={{ fontSize: '0.68rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: t.isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }}>
                  Preset Filter
                </MDTypography>
              </MDBox>
              <FormControl sx={{ minWidth: 220 }} size='small'>
                <InputLabel id='bl-filter-label' shrink>Bet Limit / Preset</InputLabel>
                <Select
                  labelId='bl-filter-label'
                  value={betLimitId}
                  onChange={handleBetLimitChange}
                  label='Bet Limit / Preset'
                  notched
                  displayEmpty
                  sx={{ minHeight: 40 }}
                >
                  <MenuItem value=''>
                    <em>All Presets</em>
                  </MenuItem>
                  {betLimits.map((bl) => (
                    <MenuItem key={bl.id} value={bl.id}>
                      {bl.preset || bl.id.slice(0, 8)}
                      {bl.presetMinAmount != null ? ` ($${fmt(bl.presetMinAmount)})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </MDBox>
          </MDBox>
        </Card>

        {/* Sessions Table */}
        <MDBox mb={3}>
          <MDBox display='flex' alignItems='center' gap={1.25} mb={2.5}>
            <MDBox sx={{ width: '3px', height: '18px', borderRadius: '2px', background: ACCENT_COLOR, flexShrink: 0 }} />
            <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', color: t.primaryText, fontSize: '0.95rem' }}>
              Sessions
            </MDTypography>
            <MDBox sx={{ flex: 1, height: '1px', background: t.divider, display: { xs: 'none', sm: 'block' } }} />
          </MDBox>

          <Card sx={cardSx}>
            <MDBox sx={panelHeaderSx(t)}>
              <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', fontSize: '0.95rem', color: t.primaryText }}>
                Session List
              </MDTypography>
              <MDBox sx={countBadgeSx(t)}>
                <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.65rem', color: t.tableHeaderColor, letterSpacing: '0.04em' }}>
                  {total} session{total !== 1 ? 's' : ''}
                </MDTypography>
              </MDBox>
            </MDBox>

            {initialLoading ? (
              <MDBox sx={{ p: { xs: 2, sm: 3 } }}>
                <Skeleton variant='rectangular' height={300} sx={{ borderRadius: '8px' }} />
              </MDBox>
            ) : sessions.length === 0 ? (
              <MDBox display='flex' flexDirection='column' alignItems='center' justifyContent='center' py={5} gap={1}>
                <MDTypography variant='body2' color='text' sx={{ opacity: 0.45 }}>
                  No sessions found for the selected filters
                </MDTypography>
              </MDBox>
            ) : (
              <TableContainer sx={scrollbarSx(t)}>
                <Table size='small' stickyHeader>
                  <TableHead>
                    <TableRow sx={tableHeadRowSx(t)}>
                      <TableCell sx={{ width: 28, pr: 0 }}>#</TableCell>
                      <TableCell>Room ID</TableCell>
                      <TableCell>Preset</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align='right'>Players</TableCell>
                      <TableCell align='right'>Blocks</TableCell>
                      <TableCell align='right'>Block ($)</TableCell>
                      <TableCell align='right'>Bids</TableCell>
                      <TableCell align='right'>Bids ($)</TableCell>
                      <TableCell align='right'>Airdrops</TableCell>
                      <TableCell align='right'>Eng. Return</TableCell>
                      <TableCell>Last Activity</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sessions.map((s, index) => {
                      const isTop = index === 0;
                      return (
                        <TableRow key={s.roomId} sx={tableRowSx(t, { index, isTop })}>
                          <TableCell sx={{ width: 28, pr: 0 }}>
                            <MDTypography variant='caption' sx={{ fontSize: '0.65rem', color: isTop ? t.rankTopColor : t.rankColor, fontWeight: isTop ? 700 : 400 }}>
                              {page * limit + index + 1}
                            </MDTypography>
                          </TableCell>
                          <TableCell>
                            <MDTypography variant='caption' fontWeight={isTop ? 'bold' : 'medium'} sx={{ fontSize: '0.78rem', color: t.primaryText, fontFamily: 'monospace' }}>
                              {s.roomId?.slice(0, 12)}…
                            </MDTypography>
                          </TableCell>
                          <TableCell>
                            <MDTypography variant='caption' sx={{ fontSize: '0.78rem', color: t.primaryText }}>
                              {s.betLimitName
                                ? `${s.betLimitName}${s.betLimitMinBid != null ? ` (${Number(s.betLimitMinBid).toFixed(8)} BTC)` : ''}`
                                : '—'}
                            </MDTypography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={s.isLive ? 'LIVE' : (s.endReason || 'Ended')}
                              size='small'
                              color={s.isLive ? 'success' : 'default'}
                              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell align='right'>
                            <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.primaryText }}>{s.playerCount}</MDTypography>
                          </TableCell>
                          <TableCell align='right'>
                            <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.primaryText }}>{s.totalBlocksMined}</MDTypography>
                          </TableCell>
                          <TableCell align='right'>
                            <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.75rem', color: t.primaryText }}>${fmt(s.totalBlockRewardUsd)}</MDTypography>
                          </TableCell>
                          <TableCell align='right'>
                            <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.primaryText }}>{s.totalBidsCount}</MDTypography>
                          </TableCell>
                          <TableCell align='right'>
                            <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.75rem', color: t.primaryText }}>${fmt(s.totalBidsUsd)}</MDTypography>
                          </TableCell>
                          <TableCell align='right'>
                            <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.primaryText }}>{s.totalAirdropsCount}</MDTypography>
                          </TableCell>
                          <TableCell align='right'>
                            <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: s.engineRemainingBtc != null ? t.primaryText : t.secondaryText }}>
                              {s.engineRemainingBtc != null ? fmtBtc(s.engineRemainingBtc) : '—'}
                            </MDTypography>
                          </TableCell>
                          <TableCell>
                            <MDTypography variant='caption' sx={{ fontSize: '0.73rem', color: t.secondaryText }}>{fmtDateShort(s.lastActivity)}</MDTypography>
                          </TableCell>
                          <TableCell>
                            {s.isLive ? (
                              <MDButton variant='text' color='success' size='small' sx={{ minWidth: 0, fontSize: '0.7rem', px: 1 }} onClick={() => navigate(`/nh-room/${s.roomId}`)}>
                                <Icon fontSize='small'>dashboard</Icon>&nbsp;Live
                              </MDButton>
                            ) : s.snapshotId ? (
                              <MDButton variant='text' color='info' size='small' sx={{ minWidth: 0, fontSize: '0.7rem', px: 1 }} onClick={() => navigate(`/nh-session/${s.snapshotId}`)}>
                                <Icon fontSize='small'>visibility</Icon>&nbsp;Snapshot
                              </MDButton>
                            ) : (
                              <MDButton variant='text' color='secondary' size='small' sx={{ minWidth: 0, fontSize: '0.7rem', px: 1 }} onClick={() => navigate(`/nh-room/${s.roomId}`)}>
                                <Icon fontSize='small'>analytics</Icon>&nbsp;Summary
                              </MDButton>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Pagination Footer */}
            {total > limit && (
              <MDBox sx={{ ...footerBarSx(t), display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <MDTypography variant='caption' sx={{ fontSize: '0.68rem', color: t.footerText }}>
                  Showing {page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total}
                </MDTypography>
                <MDBox display='flex' gap={1} alignItems='center'>
                  <MDButton variant='outlined' color='info' size='small' disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} sx={{ borderRadius: '8px', fontSize: '0.7rem', minWidth: 0, px: 1.5 }}>
                    Previous
                  </MDButton>
                  <MDTypography variant='caption' sx={{ fontSize: '0.68rem', color: t.secondaryText }}>
                    {page + 1} / {Math.ceil(total / limit)}
                  </MDTypography>
                  <MDButton variant='outlined' color='info' size='small' disabled={(page + 1) * limit >= total} onClick={() => setPage((p) => p + 1)} sx={{ borderRadius: '8px', fontSize: '0.7rem', minWidth: 0, px: 1.5 }}>
                    Next
                  </MDButton>
                </MDBox>
              </MDBox>
            )}
          </Card>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default NHSessionHistory;
